import { useState } from "react";
import { api } from "@/lib/api";
import { X, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Lead capture modal used by every marketing surface (landing + vertical pages).
 * Writes to `/api/leads` and shows an inline success state on the same modal.
 */
export default function LeadModal({ open, onClose, defaultVertical = "" }) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    vertical: defaultVertical, size: "", message: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const reset = () => {
    setDone(false);
    setForm({ name: "", email: "", phone: "", company: "",
      vertical: defaultVertical, size: "", message: "" });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4"
      onClick={() => !submitting && reset()}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl w-full max-w-lg flex flex-col max-h-[95vh] sm:max-h-[90vh] shadow-2xl"
        data-testid="lead-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-full bg-brand/10 text-brand"><Sparkles size={18}/></div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand">Talk to us</div>
              <h3 className="font-heading text-xl font-extrabold text-ink">Request a demo</h3>
            </div>
          </div>
          <button onClick={reset} disabled={submitting} className="p-2 rounded-full hover:bg-cream-200"><X size={16}/></button>
        </div>

        {done ? (
          <div className="flex-1 px-6 py-10 text-center" data-testid="lead-success">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3"/>
            <h2 className="font-heading text-2xl font-extrabold text-ink">Thanks — we'll be in touch</h2>
            <p className="text-sm text-ink-600 mt-2">Our team will reach out within one business day.</p>
            <button onClick={reset}
              className="mt-6 px-5 py-2.5 rounded-full bg-ink text-white text-sm font-semibold hover:bg-ink-700">Close</button>
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Name *</label>
                  <input data-testid="lead-name" required value={form.name}
                    onChange={e => setForm(p => ({...p, name: e.target.value}))}
                    className="mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:border-brand focus:outline-none"/>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Company</label>
                  <input data-testid="lead-company" value={form.company}
                    onChange={e => setForm(p => ({...p, company: e.target.value}))}
                    className="mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:border-brand focus:outline-none"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Work email *</label>
                  <input data-testid="lead-email" type="email" required value={form.email}
                    onChange={e => setForm(p => ({...p, email: e.target.value}))}
                    className="mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:border-brand focus:outline-none"/>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Phone</label>
                  <input data-testid="lead-phone" value={form.phone}
                    onChange={e => setForm(p => ({...p, phone: e.target.value}))}
                    className="mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:border-brand focus:outline-none"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Vertical</label>
                  <select data-testid="lead-vertical" value={form.vertical}
                    onChange={e => setForm(p => ({...p, vertical: e.target.value}))}
                    className="mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm bg-white focus:border-brand focus:outline-none">
                    <option value="">Select…</option>
                    <option value="corporate">Corporate campus</option>
                    <option value="mall">Mall / Food court</option>
                    <option value="education">College / University</option>
                    <option value="school">K-12 School</option>
                    <option value="expressway">Expressway / Highway</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Daily footfall</label>
                  <select data-testid="lead-size" value={form.size}
                    onChange={e => setForm(p => ({...p, size: e.target.value}))}
                    className="mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm bg-white focus:border-brand focus:outline-none">
                    <option value="">Select…</option>
                    <option value="<500">Under 500</option>
                    <option value="500-2k">500 – 2,000</option>
                    <option value="2k-10k">2,000 – 10,000</option>
                    <option value=">10k">Over 10,000</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Tell us about your setup</label>
                <textarea data-testid="lead-message" rows={3} value={form.message}
                  onChange={e => setForm(p => ({...p, message: e.target.value}))}
                  placeholder="Number of vendors, locations, current systems, key challenges…"
                  className="mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:border-brand focus:outline-none resize-none"/>
              </div>
            </form>
            <div className="px-6 py-3 border-t border-border shrink-0 flex justify-end gap-2">
              <button type="button" onClick={reset} className="px-5 py-2.5 rounded-full text-ink-700 text-sm font-semibold hover:bg-cream-200">Cancel</button>
              <button type="submit" data-testid="lead-submit" onClick={submit} disabled={submitting}
                className="px-6 py-2.5 rounded-full bg-brand text-white text-sm font-semibold hover:bg-brand-600 disabled:opacity-60 inline-flex items-center gap-2">
                {submitting ? <Loader2 size={14} className="animate-spin"/> : null}
                {submitting ? "Sending…" : "Request demo"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
